/**
 * Estado de entrega, recomendaciones de Meta y cobertura de datos (Ajuste 6 y 7
 * de la API). Funciones puras. Ver docs/maps/meta-ads.md § Estado de entrega.
 *
 * La API calcula todo (`delivery_*`, `recommendations_*`, `data_coverage`): acá
 * solo se lee con defaults seguros. Sin los campos (API vieja) se cae a la
 * etiqueta de `effective_status` de siempre.
 */

const DELIVERY_TONES = ['success', 'warning', 'info', 'danger', 'neutral'];

/** `delivery_tone` → tono de la píldora (`getMetaAdsStatusPillSx`). */
const PILL_TONE = {
  success: 'done',
  info: 'partial',
  warning: 'warning',
  danger: 'danger',
  neutral: 'pending',
};

const str = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);
const count = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
};
const optionalCount = (value) => (value == null ? null : count(value));

/** Punto de entrega (como la columna «Entrega» del Administrador de anuncios). */
const DOT_BY_STATUS = {
  ACTIVE_DELIVERING: 'green',
  ACTIVE_NO_DELIVERY: 'amber',
  IN_PROCESS: 'amber',
  IN_REVIEW: 'amber',
  SCHEDULED: 'amber',
  WITH_ISSUES: 'amber',
  REJECTED: 'red',
};
const DOT_BY_TONE = { success: 'green', warning: 'amber', danger: 'red' };

/**
 * Color del punto de entrega: `green` | `amber` | `red` | `gray`. Solo de
 * `delivery_status` (verde = solo ACTIVE_DELIVERING; ámbar = ACTIVE_NO_DELIVERY,
 * IN_PROCESS, IN_REVIEW, SCHEDULED, WITH_ISSUES; rojo = solo REJECTED; cualquier
 * otro código, gris); sin código, `delivery_tone`; sin nada de eso, el
 * `effective_status` viejo (ACTIVE verde, resto gris).
 * `is_delivering` NO cuenta: es «tuvo impresiones hoy o ayer», no «activa ahora»
 * (una campaña COMPLETED puede traerlo en true).
 */
export function metaDeliveryDot(raw, effectiveStatus = null) {
  const status = str(raw?.delivery_status);
  if (status) return DOT_BY_STATUS[status.toUpperCase()] || 'gray';
  const tone = str(raw?.delivery_tone);
  if (tone) return DOT_BY_TONE[tone] || 'gray';
  const effective = String(str(effectiveStatus) || str(raw?.effective_status) || str(raw?.status) || '').toUpperCase();
  return effective === 'ACTIVE' ? 'green' : 'gray';
}

/**
 * Entrega de una campaña o anuncio.
 *
 * @param {object} raw Fila de la API.
 * @param {string|null} fallbackLabel Etiqueta vieja (de `effective_status`).
 * @param {string|null} effectiveStatus `effective_status` ya leído (para el punto sin `delivery_status`).
 * @returns {{ known: boolean, status: string|null, label: string|null, tone: string, pillTone: string,
 *   rank: number|null, hint: string|null, isDelivering: boolean|null, isInactive: boolean|null,
 *   lastDeliveryDate: string|null, ended: boolean, dot: 'green'|'amber'|'red'|'gray' }}
 */
export function readMetaDelivery(raw, fallbackLabel = null, effectiveStatus = null) {
  const status = str(raw?.delivery_status);
  const label = str(raw?.delivery_label);
  const tone = DELIVERY_TONES.includes(raw?.delivery_tone) ? raw.delivery_tone : 'neutral';
  const rank = Number(raw?.delivery_rank);
  return {
    known: Boolean(status || label),
    status,
    label: label || fallbackLabel || null,
    tone,
    pillTone: PILL_TONE[tone],
    rank: Number.isFinite(rank) ? rank : null,
    hint: str(raw?.delivery_hint),
    isDelivering: typeof raw?.is_delivering === 'boolean' ? raw.is_delivering : null,
    isInactive: typeof raw?.is_inactive === 'boolean' ? raw.is_inactive : null,
    lastDeliveryDate: str(raw?.last_delivery_date),
    ended: raw?.ended === true,
    dot: metaDeliveryDot(raw, effectiveStatus),
    // Ajuste 11: la entrega se juzgó con datos guardados viejos (mismos códigos y colores).
    dataStale: raw?.delivery_data_stale === true,
    dataThrough: str(raw?.delivery_data_through),
  };
}

/**
 * Texto del Tooltip de entrega: `delivery_hint` y, con `delivery_data_stale`, «Según datos
 * al DD/MM» (salvo que el hint ya lo diga). Sin nada → ''.
 */
export function metaDeliveryTooltip(delivery) {
  if (!delivery) return '';
  const hint = delivery.hint || '';
  const m = delivery.dataStale && /^(\d{4})-(\d{2})-(\d{2})$/.exec(delivery.dataThrough || '');
  if (!m) return hint;
  const day = `${m[3]}/${m[2]}`;
  if (hint.includes(day)) return hint;
  return [hint, `Según datos al ${day}`].filter(Boolean).join(' · ');
}

/** Contadores de anuncios de una campaña (`getmetacampaigns`). `null` = la API no lo mandó. */
export function readMetaAdCounters(raw) {
  return {
    adsTotalCount: optionalCount(raw?.ads_total_count),
    hiddenAdsCount: count(raw?.hidden_ads_count),
    deliveringAdsCount: optionalCount(raw?.delivering_ads_count),
  };
}

/**
 * Solo links `https://` a facebook.com (o subdominios: business., adsmanager.).
 * Cualquier otra cosa → null (no se muestra el link).
 */
export function safeMetaFacebookUrl(value) {
  const raw = str(value);
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:') return null;
    const host = url.hostname.toLowerCase();
    if (host !== 'facebook.com' && !host.endsWith('.facebook.com')) return null;
    return url.href;
  } catch {
    return null;
  }
}

/**
 * Recomendaciones de Meta (solo lectura: se aplican en Meta, nunca desde Terrio).
 * `metaUrl` de cada una solo si es https a facebook.com (botón «Aplicar en Meta»).
 *
 * @returns {{ count: number, points: number|null, items: object[] }}
 */
export function readMetaRecommendations(raw) {
  const items = (Array.isArray(raw?.recommendations) ? raw.recommendations : [])
    .map((rec) => {
      const points = Number(rec?.points);
      return {
        id: str(rec?.recommendation_id),
        title: str(rec?.title) || 'Recomendación de Meta',
        message: str(rec?.message),
        estimatedImpact: str(rec?.estimated_impact),
        points: Number.isFinite(points) && points > 0 ? points : null,
        levelLabel: str(rec?.level_label),
        metaUrl: safeMetaFacebookUrl(rec?.meta_url),
      };
    });
  const points = Number(raw?.recommendations_points);
  return {
    count: raw?.recommendations_count == null ? items.length : count(raw.recommendations_count),
    points: Number.isFinite(points) && points > 0 ? points : null,
    items,
  };
}

const recommendationKey = (rec) => `${(rec.title || '').trim().toLowerCase()}|${(rec.message || '').trim().toLowerCase()}`;

/**
 * Una sola lista para varios niveles (campaña + sus anuncios): sin repetir la misma
 * recomendación (mismo título + mensaje; p. ej. una de conjunto de anuncios llega
 * en la campaña y en cada anuncio). Cada entrada guarda de dónde vino (`sources`).
 * Si nadie manda `recommendations[]` (solo `recommendations_count`), no se puede
 * deduplicar: se usa el mayor de los contadores (nunca la suma).
 *
 * @param {{ label: string, recommendations: object|null }[]} groups
 * @returns {{ count: number, entries: object[], multipleSources: boolean }}
 */
export function collectMetaRecommendations(groups) {
  const byKey = new Map();
  let maxCount = 0;
  (groups || []).forEach(({ label, recommendations }) => {
    maxCount = Math.max(maxCount, recommendations?.count || 0);
    (recommendations?.items || []).forEach((rec) => {
      const key = recommendationKey(rec);
      const prev = byKey.get(key);
      if (prev) {
        if (label && !prev.sources.includes(label)) prev.sources.push(label);
        if (!prev.metaUrl && rec.metaUrl) prev.metaUrl = rec.metaUrl;
        if (prev.points == null && rec.points != null) prev.points = rec.points;
      } else {
        byKey.set(key, { ...rec, key, sources: label ? [label] : [] });
      }
    });
  });
  const entries = [...byKey.values()];
  const sources = new Set(entries.flatMap((e) => e.sources));
  return { count: entries.length || maxCount, entries, multipleSources: sources.size > 1 };
}

/** «+9 puntos» / «+1 punto» (redondeado). Vacío sin puntos. */
export function formatMetaRecommendationPoints(points) {
  const n = Math.round(Number(points));
  if (!Number.isFinite(n) || n <= 0) return '';
  return `+${n} ${n === 1 ? 'punto' : 'puntos'}`;
}

/**
 * `opportunity_score` de una cuenta (0–100). `known` = la API manda el campo
 * (aunque sea null → «—»).
 */
export function readMetaOpportunityScore(raw) {
  const known = Boolean(raw && typeof raw === 'object' && 'opportunity_score' in raw);
  const n = raw?.opportunity_score == null ? NaN : Number(raw.opportunity_score);
  const score = Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : null;
  return { known, score, label: score == null ? '—' : String(score) };
}

/**
 * `data_coverage` de `getcampaignbreakdowns`. Solo devuelve nota si
 * `complete === false` (estricto: sin el campo no se avisa nada).
 */
export function readMetaDataCoverage(data) {
  const coverage = data?.data_coverage;
  if (!coverage || typeof coverage !== 'object' || coverage.complete !== false) return null;
  return {
    complete: false,
    note: str(coverage.note) || 'Algunos cortes todavía se están completando.',
    byKind: coverage.by_kind && typeof coverage.by_kind === 'object' ? coverage.by_kind : {},
  };
}
